import React from "react"
import Relay from "react-relay"
import shallowCompare from "react-addons-shallow-compare"
import Helmet from "react-helmet"
import { Link, browserHistory } from "react-router"
import classNames from "classnames"
import { find, some } from "lodash/collection"
import { findIndex } from "lodash/array"
import { trim } from "lodash/string"
import { injectIntl, FormattedMessage } from "react-intl"
import sharedMessages from "../../messages/index"
import shareMessages from "../../messages/share"
import settingsMessages from "../../messages/settings"
import resourceMessages from "../../messages/resource"

import markdown from "../../scripts/markdown"

import { isomorphicVars } from "../../scripts/isomorphicVars"
import AccordiTab from "../elements/AccordiTab/AccordiTab"
import AccordiItem from "../elements/AccordiTab/AccordiItem"
import AddFavourite from "../elements/AddFavourite"
import Badge from "../elements/Badge"
import BadgeLink from "../elements/BadgeLink"
import State from "../../stores/State"
import { dispatch } from "../../Dispatcher"
import { getPageMeta, userAgent } from "../../scripts/utils"
import { isSizeGreaterOrEqual } from "../../scripts/size"
import {
  getPDFSrcFromResource,
  getImageSrcFromResource,
  getImageSrcFromFile,
  getVideoFromResource,
  getDeepZoomSrcFromResource,
  getHeldInName,
  getImageType,
  getTranscript
} from "../../scripts/resource"
import { getResourceLabel } from "../../scripts/resourceTypes"
import smoothScroll from "../../scripts/smoothScroll"
import Icon from "../elements/Icon"
import RelatedItem from "./RelatedItem"
import ArticleMetaShare from "./ArticleMetaShare"
import PDFView from "./PDFView"
import ImageView from "./ImageView"
import AudioView from "./AudioView"
import VideoView from "./VideoView"
import MapView from "./MapView"
import { LAYER_ID } from "../../scripts/mapBox"
import { FORMAT_NAMES } from "../../constants"
import { getAccountSettings } from "../../scripts/accountSettings"
import Image from "../elements/Image"
import Hexagon from "../elements/Hexagon"
import { getStyles, getStyleTag, hexToRgbA } from "../../styles/dynamicStyles"
import { encodeBreadcrumbsParameters } from "../../routes"
import { clearKeysFromObject } from "../../utils/object-format"
import { getCommentsRedirectLink } from "../../utils/url"
import CommentsWidget from "../elements/CommentsWidget"
import { filterHTML } from "../../scripts/xss"

// Fields that are displayed separately.
// Therefore, it is necessary to filter from `custom_fields`
const excludedCustomFields = [
  "title",
  "machine_tags",
  "tags",
  "subject",
  "notes",
  "digitisation_status",
  "rightsholder",
  "coverage"
]

const getManualDisplayField = () => [
  {
    name: "format",
    title: resourceMessages.format,
    details: value => ({
      value: value in FORMAT_NAMES ? FORMAT_NAMES[value] : value
    })
  },
  {
    name: "held_in",
    title: resourceMessages.heldIn,
    details: value => ({
      link: `/search?held_in=${encodeURIComponent(getHeldInName(value))}`,
      value: getHeldInName(value)
    })
  },
  {
    name: "original_held_in",
    title: resourceMessages.relation,
    details: value => ({
      link: `/search?original_held_in=${encodeURIComponent(
        getHeldInName(value)
      )}`,
      value: trim(getHeldInName(value), '"')
    })
  },
  {
    name: "archive",
    title: resourceMessages.archive,
    details: value => ({
      link: `/search?archive=${encodeURIComponent(value)}`,
      value
    })
  }
]

const iconColors = {
  image: "#11657D",
  audio: "#A7CD41",
  video: "#167846",
  book: "#B45B29",
  map: "#000000"
}

class Resource extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      ...this._fromGlobalState(props),
      activeTab: "tab-item-description",
      transcriptTabs: true,
      showSelectPage: true,
      editLoading: false,
      thumbs: this._getThumbs(props),
      files: this._getFiles(props),
      transcripts: this._getTranscripts(props),
      activeTranscriptIndex: 0,
      signUpLink: "",
      signInLink: ""
    }

    const {
      settings: { disqus_short_name: disqusShortName }
    } = getAccountSettings(
      props.intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const {
      public_url: publicUrl,
      admin_url: adminUrl,
      api_endpoint: apiEndpoint,
      authentic_account_id: authenticAccountId
    } = isomorphicVars()
    this._public_url = publicUrl
    this._disqus_shortname = disqusShortName
    this._admin_url = adminUrl
    this._api_endpoint = apiEndpoint
    this._authentic_account_id = authenticAccountId
  }

  shouldComponentUpdate(nextProps, nextState) {
    return shallowCompare(this, nextProps, nextState)
  }

  _fromGlobalState = props => {
    props = props || this.props

    const resultIdsState = State.get("resultIds")
    const resultIds = !!resultIdsState.size
      ? resultIdsState
      : this.props.resourcesIds
    const resourceIndex = resultIds.indexOf(props.resource.id)
    const resultIdsLength = resultIds.length

    return {
      loggedIn: State.get("loggedIn"),
      nextResourceId:
        resultIds[Math.min(resultIdsLength - 1, resourceIndex + 1)],
      previousResourceId: resultIds[Math.max(resourceIndex - 1, 0)]
    }
  }

  componentDidMount() {
    this._stateListener = State.addListener(() => {
      this.setState({
        ...this._fromGlobalState(),
        transcriptTabs: isSizeGreaterOrEqual("large"),
        editLoading: false
      })
    })

    //Load tab if tab hash is in url
    if (document.location.hash) {
      const hash = document.location.hash.substr(1)
      if (hash.match(/^tab/)) {
        this.setState({ activeTab: hash })
      }
    }

    this.setState({
      transcriptTabs: isSizeGreaterOrEqual("large"),
      signUpLink: getCommentsRedirectLink("register"),
      signInLink: getCommentsRedirectLink("login")
    })
  }

  componentWillMount() {
    this._showSelectPage(this.props.child || this.props.file ? false : true)
    this._propsDispatch()
  }

  componentWillReceiveProps(props) {
    if (props.resource.id !== this.props.resource.id) {
      //console.log("New page, start out assuming we don't need to select")
      this.props.relay.setVariables({ showingShareModal: false })
      this.setState({
        ...this._fromGlobalState(props),
        showSelectPage: false,
        activeTab: "tab-item-description",
        transcripts: this._getTranscripts(props),
        thumbs: this._getThumbs(props),
        files: this._getFiles(props)
      })
    } else if (
      (!props.child && this.props.child) ||
      (!props.file && this.props.file)
    ) {
      // console.log("moving from child/file to no child/file, show select page")
      this._showSelectPage(true)
    } else if (
      (props.child && !this.props.child) ||
      (props.file && !this.props.file)
    ) {
      // console.log("moving from select page to child/file page, don't show select")
      this._showSelectPage(false)
    }

    //Activate transcript tab if applicable
    this._activateTranscript(props.child)
    this._propsDispatch(props)
  }

  componentWillUnmount() {
    dispatch({ type: "view-disable-header-pad", value: false })

    if (this._stateListener) {
      this._stateListener.remove()
    }
  }

  _hasMedia = props => {
    props = props || this.props
    return (
      props.resource.files.length > 0 ||
      some(props.resource.children.edges, child => child.node.files.length > 0)
    )
  }

  _reducedHeader = props => {
    props = props || this.props

    var video = false
    if (this.props.resource.type === "video") {
      video = getVideoFromResource(props.resource)
    }
    const hasVideo = video && video.url ? true : false

    // determine if we should use the reduced header or not. currently:
    // reduce header if hasMedia, UNLESS there is also a video
    return this._hasMedia(props) && !hasVideo ? true : false
  }

  _propsDispatch = props => {
    /**
     * OK OK OK  HACK
     *  This ( Dispatching based on props ) should never be done.
     * But we have to because Toivo ( x ) loves having all the classes in the body tag
     * The only way the body tag can know about whats going on in this component is to send a message up
     * And what we send up depends on our props.
     *
     * Kind of a problem unique to Relay, as this component fetches props that a parent component has
     * no knowledge of.
     *
     * To make this work at all I have to put it in a setTimeout, so that it is fired on the next tick
     * otherwise react will recognise this as a dispatch during a dispatch and block it.
     */

    props = props || this.props

    setTimeout(() => {
      dispatch({
        type: "view-disable-header-pad",
        value: this._reducedHeader(props)
      })
    }, 1)
  }

  _handleActivateTab = tab => {
    this.setState(
      {
        activeTab: tab
      },
      () => {
        history.replaceState(
          { activeTab: tab },
          "",
          document.location.pathname + document.location.search + "#" + tab
        )
      }
    )
  }

  _getActivePage = props => {
    props = props || this.props

    const nodes =
      props.resource.children.edges &&
      props.resource.children.edges.map(child => child.node)

    return (
      (props.child && nodes && find(nodes, { id: props.child })) ||
      props.resource
    )
  }

  _getActiveFile = props => {
    props = props || this.props

    const files = this.state.files

    return (
      (props.file && files && find(files, { id: props.file })) || props.resource
    )
  }

  _setActiveChild = uuid => {
    browserHistory.push(
      "/item/" + this.props.resource.id + (uuid && "?child=" + uuid)
    )
  }

  _setActiveFile = uuid => {
    browserHistory.push(
      "/item/" + this.props.resource.id + (uuid && "?file=" + uuid)
    )
  }

  _clearActiveChildFile = () => {
    browserHistory.push("/item/" + this.props.resource.id)
  }

  _showSelectPage = show => {
    if (this.state.showSelectPage !== show) {
      this.setState(
        {
          showSelectPage: show
        },
        () => {
          if (show && (this.props.child || this.props.file)) {
            //We get here when we click show grid as don't want to drive that via componentWillReceiveProps
            //This fade timeout prevents the cover resource jumping in as we fade the child/file out
            setTimeout(() => {
              this._clearActiveChildFile()
            }, 400) //NOTE: unfortunately, the timing of this should be kept aligned with the timing of c-mviewer__single, in mviewer.scss
          }
        }
      )
    }
  }

  _activateNextResource = (up = true) => {
    const thumbs = this.state.thumbs
    const length = thumbs.length

    const current = findIndex(thumbs, {
      id: this.props.child || this.props.resource.id
    })
    const next = up ? current + 1 : current - 1

    if (next >= length) {
      //Cycle back to first
      this._setActiveChild(thumbs[0].id)
      // this._showSelectPage(true)
    } else if (next < 0) {
      //Cycle back to last
      this._setActiveChild(thumbs[length - 1].id)
      // this._showSelectPage(true)
    } else {
      this._setActiveChild(thumbs[next].id)
    }
  }

  _activateNextFile = (up = true) => {
    const files = this.state.files
    const length = files.length

    const current = findIndex(files, {
      id: this.props.file || this.props.resource.id
    })
    const next = up ? current + 1 : current - 1

    if (next >= length) {
      //Cycle back to first
      this._setActiveFile(files[0].id)
      // this._showSelectPage(true)
    } else if (next < 0) {
      //Cycle back to last
      this._setActiveFile(files[length - 1].id)
      // this._showSelectPage(true)
    } else {
      this._setActiveFile(files[next].id)
    }
  }

  //If this Resource has a transcript, activate the transcript tab.
  _activateTranscript = resourceId => {
    const index = findIndex(this.state.transcripts, { id: resourceId })

    if (index > -1) {
      this.setState({
        activeTab: "tab-item-transcript",
        activeTranscriptIndex: index
      })
    }

    return index
  }

  _getThumbs = props => {
    return this._parentAndChildren(this._findThumbs, props)
  }

  _getFiles = props => {
    props = props || this.props

    const files = props.resource.files

    return files.reduce(this._findFiles, [])
  }

  _getTranscripts = props => {
    return this._parentAndChildren(this._findTranscripts, props)
  }

  //Create a single array of this parent and its children, and reduce it with fn
  _parentAndChildren = (fn, props) => {
    props = props || this.props

    const nodes =
      props.resource.children &&
      props.resource.children.edges &&
      props.resource.children.edges.map(child => child.node)

    return [props.resource].concat(nodes || []).reduce(fn, [])
  }

  //Used as reduce function in _getThumbs(), _getThumbs() is called via render, so I'm thinking this shouldn't be defined each time its called.
  _findThumbs = (thumbs = [], resource) => {
    const src = getImageSrcFromResource(
      resource,
      "small",
      getImageType(resource),
      false
    )

    if (src) {
      const isChild = resource.id !== this.props.resource.id

      thumbs.push({
        id: resource.id,
        resource: resource,
        src: src,
        href: "/item/" + this.props.resource.id + "?child=" + resource.id,
        isChild: isChild,
        title: resource.title,
        position: resource.position
      })
    }

    return thumbs
  }

  _findFiles = (files = [], file) => {
    // Don't show cover_image, captions, or transcript files
    if (
      file.type === "cover_image" ||
      file.type === "captions" ||
      file.type === "transcription"
    ) {
      return files
    }

    const src = getImageSrcFromFile(file, "small", getImageType(file), false)
    const iconColor = iconColors[file.type] || "#000"
    const triangleBgColor = {
      "--triangle-bg-color": hexToRgbA(iconColor, 0.8)
    }

    files.push({
      id: file.id,
      file: file,
      src: src,
      href: "/item/" + this.props.resource.id + "?file=" + file.id,
      type: file.type,
      iconColor,
      triangleBgColor
    })

    return files
  }

  _findTranscripts = (transcripts = [], resource) => {
    const text = getTranscript(resource)
    if (text) {
      transcripts.push({
        parent: !!resource.children,
        text: text,
        title: resource.title,
        id: resource.id,
        getPosition: () =>
          resource.position ||
          findIndex(this.state.thumbs, { id: resource.id }) + 1
      })
    }
    return transcripts
  }

  _handleLoadMore = e => {
    e.preventDefault()

    this.props.relay.setVariables(
      {
        pageSize: this.props.relay.variables.pageSize + 15
      },
      readyState => {
        const loading = !readyState.done

        if (loading !== this.state.loading) {
          this.setState({
            loadingMore: loading
          })
        }
      }
    )
  }

  openShareModal = (tab, files) => e => {
    e.preventDefault()
    this.props.relay.setVariables({
      showingShareModal: true,
      shareModalTab: tab
    })
  }

  closeShareModal = () =>
    this.props.relay.setVariables({
      showingShareModal: false
    })

  onZoomIn = e => {
    const {
      resource: { type }
    } = this.props
    if (type === "map" && this.mapRef) {
      e.stopPropagation()
      this.mapRef.zoomIn()
    } // else even will propagate and be caught by deepZoom
  }

  onZoomOut = e => {
    const {
      resource: { type }
    } = this.props
    if (type === "map" && this.mapRef) {
      e.stopPropagation()
      this.mapRef.zoomOut()
    } // else even will propagate and be caught by deepZoom
  }

  onOpacitySlide = e => {
    const {
      resource: { type }
    } = this.props
    if (type === "map" && this.mapRef) {
      this.mapRef.setPaintProperty(
        LAYER_ID,
        "raster-opacity",
        parseInt(e.target.value, 10) / 100
      )
    }
  }

  onOpacityClick = (e, direction) => {
    const {
      resource: { type }
    } = this.props
    if (type === "map" && this.mapRef) {
      let opacity

      if (direction === "up") {
        const newVal =
          this.mapRef.getPaintProperty(LAYER_ID, "raster-opacity") + 0.1 || 1 // providing fallback as on first press, the getPaintProperty value from mapbox can be undefined
        opacity = newVal > 1 ? 1 : newVal
      } else if (direction === "down") {
        const newVal =
          this.mapRef.getPaintProperty(LAYER_ID, "raster-opacity") - 0.1 || 0.9 // providing fallback as on first press, the getPaintProperty value from mapbox can be undefined
        opacity = newVal < 0 ? 0 : newVal
      }

      this.mapRef.setPaintProperty(LAYER_ID, "raster-opacity", opacity)
    }
  }

  renderResourceTitle = ({ styleBodyType }) => {
    const { resource } = this.props

    return (
      <div className="row column">
        <header
          className="c-article__header"
          style={getStyles(["c-article__header", "c-article__resource_header"])}
        >
          {styleBodyType !== "default" && (
            <Hexagon icon={resource.type_icon} color="#fff" />
          )}
          <div style={getStyles("c-article__title-wrap")}>
            <h1
              className="c-article__title"
              style={getStyles([
                "c-article__title",
                "c-article__resource-title"
              ])}
            >
              <BadgeLink
                extraClasses="hide-for-print more-link"
                onClick={e => {
                  e.preventDefault()
                  smoothScroll(document.getElementById("item-info"))
                }}
                href="#item-info"
                style={getStyles("c-article__more-info")}
              >
                <FormattedMessage {...sharedMessages.moreInfo} />
              </BadgeLink>
              <span
                style={getStyles("c-article__resource-title-name")}
                itemProp="name"
                dangerouslySetInnerHTML={{ __html: filterHTML(resource.title) }}
              />
            </h1>
            {styleBodyType !== "default" && (
              <div style={getStyles("c-article__recource-type")}>
                <FormattedMessage {...resourceMessages.objectType} />:
                <span style={getStyles("c-article__recource-type-name")}>
                  {getResourceLabel(resource.type)}
                </span>
              </div>
            )}
          </div>
        </header>
      </div>
    )
  }

  getCustomFields = () => {
    const { resource } = this.props

    if (resource.custom_fields) {
      const manualFields = getManualDisplayField()

      try {
        const fields = Object.entries(
          clearKeysFromObject(
            JSON.parse(resource.custom_fields),
            excludedCustomFields
          )
        ).flatMap(
          ([
            name,
            { internal, type, value, title, public_placement, public_position }
          ]) => {
            if (internal) {
              return []
            }

            if (!value) {
              return []
            }

            const manualField = manualFields.find(field => field.name === name)
            const manualFieldDetails = manualField
              ? manualField.details(value)
              : null
            const field = manualFieldDetails
              ? {
                  title: manualField.title,
                  link: manualFieldDetails.link,
                  value: manualFieldDetails.value
                }
              : null

            return {
              type,
              value,
              name,
              // If the title is "Digitisation", we should use the "Digitisation ID" title
              title:
                title === "Digitisation"
                  ? resourceMessages.digitisationID
                  : title,
              publicPlacement: public_placement,
              publicPosition: public_position,
              manualField: field || null
            }
          }
        )

        return fields
      } catch (error) {
        console.error(error)
      }
    }

    return []
  }

  getNavigationUrl = resourceId => {
    const isFromCollections = this.props.isFromCollections || false

    if (isFromCollections) {
      return `/collections/${this.props.urlParams.uuid}/${resourceId}`
    }

    return `/item/${resourceId}`
  }

  filterCustomFieldsByPlacement = (customFieldsProp, placement) => {
    return customFieldsProp.filter(
      ({ publicPlacement }) => publicPlacement === placement
    )
  }

  sortCustomFieldsByPosition = (customFieldsProp, placement) => {
    const sortedCustomFields =
      placement === "description"
        ? [
            ...customFieldsProp.filter(
              customField => customField.name === "description"
            ),
            ...customFieldsProp
              .filter(customField => customField.name !== "description")
              .sort((a, b) => a.publicPosition - b.publicPosition)
          ]
        : customFieldsProp.sort((a, b) => a.publicPosition - b.publicPosition)

    return sortedCustomFields
  }

  mapCustomFields = (customFieldsProp, placement) => {
    return customFieldsProp.map(
      ({ title, value, type, name, manualField }, index) => {
        if (manualField) {
          return (
            <div
              key={`${type}-${index}-${value}`}
              className="list__def c-article__defs-col"
              style={
                placement === "description"
                  ? {
                      width: "100%"
                    }
                  : {}
              }
            >
              <dt>
                <FormattedMessage {...manualField.title} />
              </dt>
              <dd>
                {"link" in manualField ? (
                  <Link
                    to={manualField.link}
                    onClick={() => {
                      dispatch({
                        type: "append-search-filter-without-query",
                        name: name,
                        value: manualField.value
                      })
                    }}
                  >
                    {manualField.value}
                  </Link>
                ) : (
                  manualField.value
                )}
              </dd>
            </div>
          )
        }

        let component = ""
        switch (type) {
          case "suggest":
            const items = Object.values(value)

            if (!items.length) {
              return null
            }

            component = items.map((val, index) => {
              const link = `/search?tags=${val}`

              return (
                <span key={`${type}-${index}-${val}`}>
                  {!!index && ", "}
                  <Link to={link}>{val}</Link>
                </span>
              )
            })
            break
          case "textarea":
            component = (
              <div
                dangerouslySetInnerHTML={{
                  __html: filterHTML(markdown.render(value))
                }}
              />
            )
            break
          case "breadcrumbs":
            const values = Object.values(value)
            component = values.map((v1, i1) => {
              const searchValues = values.filter((_, i2) => i2 <= i1)
              const breadcrumbsSearch = encodeBreadcrumbsParameters(
                name,
                searchValues
              )
              const link = `/search?${name}=${breadcrumbsSearch}`

              return (
                <span key={`${type}-${index}-${v1.key}`}>
                  {i1 > 0 && " > "}
                  <Link
                    to={link}
                    onClick={() => {
                      dispatch({
                        type: "set-breadcrumbs-term-without-query",
                        name: name,
                        breadcrumbsSearch: decodeURIComponent(breadcrumbsSearch)
                      })
                    }}
                  >
                    {v1.title}
                  </Link>
                </span>
              )
            })
            break
          case "lookup":
            component = title.toLowerCase() === "license" ? null : value
            break
          case "text":
          case "radiolist":
          case "number":
          case "edtf":
            component = value
            break
          default:
        }

        return (
          component && (
            <div
              key={`${type}-${index}-${value}`}
              className="list__def c-article__defs-col"
              style={
                placement === "description"
                  ? {
                      width: "100%"
                    }
                  : {}
              }
            >
              {/* If the title is "Digitisation", we should use the "Digitisation ID" title */}
              <dt>
                {typeof title === "string" ? (
                  title
                ) : (
                  <FormattedMessage {...title} />
                )}
              </dt>
              {component && <dd>{component}</dd>}
            </div>
          )
        )
      }
    )
  }

  renderCustomFieldsByPlacement = (customFieldsProp, placement) => {
    const filteredCustomFields = this.filterCustomFieldsByPlacement(
      customFieldsProp,
      placement
    )
    const sortedCustomFields = this.sortCustomFieldsByPosition(
      filteredCustomFields,
      placement
    )
    const mappedCustomFields = this.mapCustomFields(
      sortedCustomFields,
      placement
    )

    return mappedCustomFields
  }

  render() {
    const {
      resource,
      file,
      child,
      viewer,
      intl,
      relay: {
        variables: { showingShareModal, shareModalTab }
      }
    } = this.props
    const {
      thumbs,
      files,
      loggedIn,
      nextResourceId,
      previousResourceId
    } = this.state

    const numChildren = thumbs.length
    const numFiles = files.length
    const hasChildren = numChildren > 1
    let hasMultipleFiles = numFiles > 1

    // Rule Exception: If we have a book and an image, we want to show the book, and the image as the cover
    const bookFile =
      files.length === 2 && files.some(file => file.type === "book")
        ? files.filter(file => file.type === "book")
        : null
    const coverImageFile =
      files.length === 2 && bookFile
        ? files.filter(file => file.type === "image")
        : null
    if (bookFile && coverImageFile) {
      hasMultipleFiles = false
    }

    // if we've only got exactly one thumb, make it active, otherwise call getAccessPage which will check this.props.child, then default to resource ( which will be the parent on a multipage, or just the dude on a single )
    const activeResourcePage =
      thumbs.length === 1 ? thumbs[0].resource : this._getActivePage()
    const activeFilePage = files.length === 1 ? files[0] : this._getActiveFile()

    // If a multipage, with no child selected, media is form the first child
    const activeResourceMedia =
      !this.props.child && thumbs.length > 0
        ? thumbs[0].resource
        : activeResourcePage
    const activeFileMedia =
      !this.props.file && files.length > 0 ? files[0].file : activeFilePage.file

    const resourceType = activeResourcePage.type
    const fileType = activeFilePage.type

    const video = getVideoFromResource(activeResourceMedia)
    const imageType = getImageType(activeResourceMedia)
    const imageSrc = getImageSrcFromResource(
      activeResourceMedia,
      "medium",
      imageType,
      false
    )
    const deepZoomUrl = getDeepZoomSrcFromResource(activeResourceMedia)
    const pdfSource = getPDFSrcFromResource(activeResourceMedia)
    const showPdf = !!pdfSource && !hasChildren && !hasMultipleFiles //True if we have a value for pdfSource

    const haveFiles = this._hasMedia()

    const showSelectPage =
      this.state.showSelectPage && (hasChildren || hasMultipleFiles)

    const transcript = this.state.transcripts.filter(
      transcript => transcript.parent
    )[0]
    const childTranscripts = this.state.transcripts.filter(
      transcript => !transcript.parent
    )
    const showTranscripts = this.state.transcripts.length > 0

    const {
      finding_aid_children: findingAidChildren,
      finding_aid_parents: findingAidParents,
      related
    } = resource
    const { description, map_url } = JSON.parse(resource.custom_fields)
    const canEdit = this.props.canEdit

    //if we want the page/tabs to overlap the media viewer or not.
    const overlapMediaArea = video && video.url ? false : true
    //console.log('render resource');

    /* if have children, we make this div exist on page always so that we can css transition nicely between grid/single views */
    const withImageResourceView =
      !file &&
      (hasChildren ||
        (!(
          resourceType === "audio" ||
          resourceType === "video" ||
          resourceType === "map"
        ) &&
          !showSelectPage &&
          !showPdf))
    const withImageFileView =
      hasMultipleFiles &&
      !child &&
      !(
        file &&
        (fileType === "book" ||
          fileType === "audio" ||
          fileType === "video" ||
          fileType === "map")
      )

    const withZoomButtons =
      ((deepZoomUrl || resourceType === "map") && !showSelectPage) ||
      (hasMultipleFiles && fileType === "image")
    const withMapOpacity =
      (resourceType === "map" &&
        map_url &&
        map_url.value &&
        !showSelectPage &&
        !userAgent.isIE) ||
      (hasMultipleFiles && fileType === "map")

    const accountSettings = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const styleBodyType = accountSettings.settings.style.content || ""

    const customFields = this.getCustomFields()

    const commentsIframeUrl = `https://d2iun5pubia9jk.cloudfront.net/index.html?apiEndpoint=${
      this._api_endpoint
    }&resourceId=${resource.id}&authenticAccountId=${
      accountSettings.authentic_account_id
    }&userFullName=${this.props.user.name || ""}&signUpLink=${
      this.state.signUpLink
    }&signInLink=${this.state.signInLink}&token=${State.get(
      "tokenString"
    )}&comments_login_prompt=${accountSettings.settings.comments_login_prompt}#tab-item-comments`

    // Check if the user is on the top museum project to display Source Id field
    const isConTopMuseumProject = this._authentic_account_id === 17242

    // Check if the book exists in the files with cover image or not
    const isBookExists = files.some(file => file.type === "book")

    return (
      <main data-article-style="item" tabIndex={0} role="main" id="main">
        {getStyleTag([
          "c-article",
          "c-article__content",
          "c-article__details",
          "c-mviewer__tools",
          "c-mviewer__multi-image-item",
          "c-mviewer__multi-page-num",
          "data-article-type-flag",
          "data-article-type-icon",
          "button--lowlight"
        ])}
        <Helmet
          title={activeResourcePage.title}
          meta={getPageMeta({
            title: activeResourcePage.title,
            description: !!description ? description.value : undefined,
            image: imageSrc
          })}
        />
        <article
          data-article-type={resource.type}
          className="c-article"
          itemScope
          itemType="http://schema.org/MediaObject"
        >
          {nextResourceId &&
            nextResourceId !== resource.id && (
              <Link
                className="c-article__resource-nav c-article__resource-nav--next"
                to={this.getNavigationUrl(nextResourceId)}
                title="Next Resource"
                style={getStyles("c-article__resource-nav")}
              >
                <Icon icon={"caret-right"} />
              </Link>
            )}
          {previousResourceId &&
            previousResourceId !== resource.id && (
              <Link
                className="c-article__resource-nav c-article__resource-nav--previous"
                to={this.getNavigationUrl(previousResourceId)}
                title="Previous Resource"
                style={getStyles("c-article__resource-nav")}
              >
                <Icon icon={"caret-left"} />
              </Link>
            )}
          {haveFiles && (
            <div
              className={classNames("c-article__featured-media", {
                "state-has-overlap": overlapMediaArea
              })}
            >
              <div
                data-media-type={resourceType}
                className={classNames("c-mviewer", {
                  "state-grid": showSelectPage,
                  "state-single": !showSelectPage
                })}
                style={
                  !showSelectPage &&
                  (resourceType === "audio" || fileType === "audio")
                    ? {}
                    : getStyles("c-mviewer")
                }
              >
                {hasMultipleFiles ? (
                  <div className="c-mviewer__multi">
                    <div className="c-mviewer__multi-inner row column">
                      <ul
                        className={
                          "c-mviewer__multi-image-list c-mviewer__multi-image-list--" +
                          numFiles
                        }
                      >
                        {files.map(file => (
                          <li
                            key={file.id}
                            className="c-mviewer__multi-image-item"
                          >
                            <Link
                              to={file.href}
                              title={resource.title}
                              tabIndex={showSelectPage ? 0 : -1}
                            >
                              <div
                                className="c-article__media-flag"
                                style={{
                                  ...getStyles("c-article__media-flag"),
                                  ...(file.iconColor && file.triangleBgColor)
                                }}
                              >
                                <Icon
                                  icon={file.type || "article-type"}
                                  color={file.iconColor || "black"}
                                />
                              </div>
                              <Image src={file.src} alt={resource.title} />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : null}
                {hasChildren ? (
                  <div className="c-mviewer__multi">
                    <div className="c-mviewer__multi-inner row column">
                      <ul
                        className={
                          "c-mviewer__multi-image-list c-mviewer__multi-image-list--" +
                          numChildren
                        }
                      >
                        {thumbs.map(thumb => (
                          <li
                            key={thumb.id}
                            className="c-mviewer__multi-image-item"
                          >
                            <Link
                              to={thumb.href}
                              title={thumb.title}
                              tabIndex={showSelectPage ? 0 : -1}
                            >
                              <Image src={thumb.src} alt={thumb.title} />
                              {thumb.position && (
                                <span className="c-mviewer__multi-page-num">
                                  <Badge>{thumb.position}</Badge>
                                </span>
                              )}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ) : null}
                {withImageResourceView ? (
                  <div
                    className="c-mviewer__single c-mviewer__layout--fixed"
                    style={getStyles("c-mviewer__layout--fixed")}
                  >
                    {getStyleTag("c-mviewer__layout--fixed")}
                    <ImageView
                      key={activeResourceMedia.id}
                      resource={activeResourceMedia}
                      file={null}
                      withDeepZoom={!showSelectPage}
                      imgClassName="state-canvas-contain"
                    />
                  </div>
                ) : null}
                {withImageFileView ? (
                  <div
                    className="c-mviewer__single c-mviewer__layout--fixed"
                    style={getStyles("c-mviewer__layout--fixed")}
                  >
                    {getStyleTag("c-mviewer__layout--fixed")}
                    <ImageView
                      key={activeFileMedia.id}
                      file={activeFileMedia}
                      resource={null}
                      withDeepZoom={!showSelectPage}
                      imgClassName="state-canvas-contain"
                    />
                  </div>
                ) : null}

                {!showSelectPage && !file ? (
                  <div>
                    {resourceType === "book" || isBookExists ? (
                      <div className="c-mviewer__single c-mviewer__layout--fixed">
                        {getStyleTag("c-mviewer__layout--fixed")}
                        <PDFView
                          key={activeResourceMedia.id}
                          resource={activeResourceMedia}
                          viewer={viewer}
                        />
                      </div>
                    ) : null}
                    {resourceType === "audio" && (
                      <div className="c-mviewer__layout--fixed">
                        key={activeResourceMedia.id}
                        <AudioView
                          key={activeResourceMedia.id}
                          resource={activeResourceMedia}
                        />
                      </div>
                    )}
                    {resourceType === "video" && (
                      <div
                        className={classNames({
                          "c-mviewer__layout--flow": video && video.url,
                          "c-mviewer__layout--fluid": !(video && video.url)
                        })}
                      >
                        <div className="c-mviewer__video">
                          <VideoView
                            key={activeResourceMedia.id}
                            resource={activeResourceMedia}
                            file={null}
                          />
                        </div>
                      </div>
                    )}
                    {resourceType === "map" && (
                      <div className="c-mviewer__layout--fixed">
                        <MapView
                          key={activeResourceMedia.id}
                          resource={activeResourceMedia}
                          mapRef={map => (this.mapRef = map)}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
                {!showSelectPage && file ? (
                  <div>
                    {fileType === "book" && (
                      <div className="c-mviewer__single c-mviewer__layout--fixed">
                        {getStyleTag("c-mviewer__layout--fixed")}
                        <PDFView
                          key={activeFileMedia.id}
                          file={file}
                          resource={activeResourceMedia}
                          viewer={viewer}
                        />
                      </div>
                    )}
                    {fileType === "audio" && (
                      <div className="c-mviewer__single c-mviewer__layout--fixed">
                        {getStyleTag("c-mviewer__layout--fixed")}
                        <AudioView
                          key={activeFileMedia.id}
                          file={activeFileMedia}
                        />
                      </div>
                    )}
                    {fileType === "video" && (
                      <div
                        className="c-mviewer__layout--flow"
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          position: "relative",
                          zIndex: 10
                        }}
                      >
                        <div className="c-mviewer__video">
                          <VideoView
                            key={activeFileMedia.id}
                            file={activeFileMedia}
                            resource={null}
                          />
                        </div>
                      </div>
                    )}
                    {fileType === "map" && (
                      <div className="c-mviewer__single c-mviewer__layout--fixed">
                        {getStyleTag("c-mviewer__layout--fixed")}
                        <MapView
                          key={activeFileMedia.id}
                          resource={activeResourceMedia}
                          mapRef={map => (this.mapRef = map)}
                        />
                      </div>
                    )}
                  </div>
                ) : null}
                {!showPdf && (
                  <div
                    className="c-mviewer__tools"
                    style={(getStyles("c-mviewer__tools"), { zIndex: 20 })}
                  >
                    <button
                      className="button"
                      title={intl.formatMessage(shareMessages.share)}
                      onClick={this.openShareModal("share")}
                    >
                      <Icon icon="share" />
                    </button>
                    {!isConTopMuseumProject && (
                      <span>
                        <button
                          className="button"
                          title={intl.formatMessage(shareMessages.download)}
                          onClick={this.openShareModal("download")}
                        >
                          <Icon icon="download" />
                        </button>
                        <AddFavourite
                          viewer={viewer}
                          resource={resource}
                          className="button"
                        />
                      </span>
                    )}
                    {withZoomButtons && (
                      <span className="zoom-wrap">
                        <button
                          id="sd-zoom-out"
                          className="button"
                          onClick={this.onZoomOut}
                        >
                          <Icon icon="zoom-out" />
                        </button>
                        <button
                          id="sd-zoom-in"
                          className="button"
                          onClick={this.onZoomIn}
                        >
                          <Icon icon="zoom-in" />
                        </button>
                      </span>
                    )}
                    {withMapOpacity && (
                      <span
                        className="c-mviewer__map-tool-opacity"
                        style={getStyles("c-mviewer__map-tool-opacity")}
                      >
                        <label className="c-mviewer__map-tool-opacity-label-long">
                          <FormattedMessage {...sharedMessages.mapOpacity} />
                        </label>
                        <label
                          className="c-mviewer__map-tool-opacity-label-short"
                          style={getStyles(
                            "c-mviewer__map-tool-opacity-label-short"
                          )}
                        >
                          <FormattedMessage {...sharedMessages.opacity} />
                        </label>
                        <input
                          onChange={this.onOpacitySlide}
                          id="map-opacity-slider"
                          className="c-mviewer__map-tool-opacity-slider"
                          type="range"
                          min="0"
                          max="100"
                          step="0"
                          defaultValue="100"
                        />
                        <span className="c-mviewer__map-tool-opacity-buttons">
                          <button
                            className="button"
                            title={intl.formatMessage(
                              sharedMessages.mapOpacityDown
                            )}
                            onClick={e => this.onOpacityClick(e, "down")}
                          >
                            <Icon icon="opacity-down" />
                          </button>
                          <button
                            className="button"
                            title={intl.formatMessage(
                              sharedMessages.mapOpacityUp
                            )}
                            onClick={e => this.onOpacityClick(e, "up")}
                          >
                            <Icon icon="opacity-up" />
                          </button>
                        </span>
                      </span>
                    )}
                    {hasChildren &&
                      (deepZoomUrl || imageSrc) && (
                        <span>
                          <button
                            id="sd-previous"
                            onClick={e => {
                              e.preventDefault()
                              this._activateNextResource(false)
                            }}
                            className="button"
                            title={intl.formatMessage(
                              sharedMessages.previousPageImage
                            )}
                          >
                            <Icon icon="caret-left" />
                          </button>

                          <button
                            id="mv-home"
                            onClick={e => {
                              e.preventDefault()
                              this._showSelectPage(true)
                            }}
                            className="button"
                            title={intl.formatMessage(
                              sharedMessages.allPageImage
                            )}
                          >
                            <Icon icon="view-grid" />
                          </button>

                          <button
                            id="sd-next"
                            onClick={e => {
                              e.preventDefault()
                              this._activateNextResource(true)
                            }}
                            className="button"
                            title={intl.formatMessage(
                              sharedMessages.nextPageImage
                            )}
                          >
                            <Icon icon="caret-right" />
                          </button>
                        </span>
                      )}
                    {/* controls to multiple files */}
                    {hasMultipleFiles && (
                      <span>
                        <button
                          id="sd-previous"
                          onClick={e => {
                            e.preventDefault()
                            this._activateNextFile(false)
                          }}
                          className="button"
                          title={intl.formatMessage(
                            sharedMessages.previousPageImage
                          )}
                        >
                          <Icon icon="caret-left" />
                        </button>

                        <button
                          id="mv-home"
                          onClick={e => {
                            e.preventDefault()
                            this._showSelectPage(true)
                          }}
                          className="button"
                          title={intl.formatMessage(
                            sharedMessages.allPageImage
                          )}
                        >
                          <Icon icon="view-grid" />
                        </button>

                        <button
                          id="sd-next"
                          onClick={e => {
                            e.preventDefault()
                            this._activateNextFile(true)
                          }}
                          className="button"
                          title={intl.formatMessage(
                            sharedMessages.nextPageImage
                          )}
                        >
                          <Icon icon="caret-right" />
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div
            className={classNames("row column c-tabs c-tabs--pagey", {
              "js-trigger-chrome": this._reducedHeader()
            })}
          >
            {getStyleTag([
              "c-article-data__item--topOffset",
              "c-tabs__tab",
              "c-tabs--pagey__tab"
            ])}
            <nav className="c-tabs__nav">
              <a
                href="#tab-item-description"
                className={classNames("c-tabs__tab", {
                  "state-active":
                    this.state.activeTab === "tab-item-description"
                })}
                style={getStyles([
                  "c-tabs__tab",
                  "c-tabs__tab__article",
                  this.state.activeTab === "tab-item-description"
                    ? "c-tabs__tab__active"
                    : ""
                ])}
                onClick={e => {
                  e.preventDefault()
                  this._handleActivateTab("tab-item-description")
                }}
              >
                <FormattedMessage {...sharedMessages.tabDescription} />
              </a>
              {showTranscripts && (
                <a
                  href="#tab-item-transcript"
                  className={classNames("c-tabs__tab", {
                    "state-active":
                      this.state.activeTab === "tab-item-transcript"
                  })}
                  style={getStyles([
                    "c-tabs__tab",
                    "c-tabs__tab__article",
                    this.state.activeTab === "tab-item-transcript"
                      ? "c-tabs__tab__active"
                      : ""
                  ])}
                  onClick={e => {
                    e.preventDefault()
                    this._handleActivateTab("tab-item-transcript")
                  }}
                >
                  <FormattedMessage {...sharedMessages.tabTranscript} />
                </a>
              )}
              <a
                href="#tab-item-comments"
                className={classNames("c-tabs__tab", {
                  "state-active": this.state.activeTab === "tab-item-comments"
                })}
                style={getStyles([
                  "c-tabs__tab",
                  "c-tabs__tab__article",
                  this.state.activeTab === "tab-item-comments"
                    ? "c-tabs__tab__active"
                    : ""
                ])}
                onClick={e => {
                  e.preventDefault()
                  this._handleActivateTab("tab-item-comments")
                }}
              >
                {accountSettings.settings.comments_tab_title ? (
                  accountSettings.settings.comments_tab_title
                ) : (
                  <FormattedMessage {...sharedMessages.tabComments} />
                )}
              </a>
            </nav>
            <div
              id="tab-item-description"
              className={classNames("c-tabs__content", {
                "state-active": this.state.activeTab === "tab-item-description"
              })}
              style={getStyles("c-tabs__content")}
            >
              {styleBodyType === "default" &&
                this.renderResourceTitle({ styleBodyType })}
              <div className="row" id="item-info">
                <div className="small-12 medium-7 columns">
                  {styleBodyType !== "default" &&
                    this.renderResourceTitle({ styleBodyType })}

                  <section className="c-article__details c-article__details--maincol">
                    <dl className="c-article__defs">
                      {this.renderCustomFieldsByPlacement(
                        customFields,
                        "description"
                      )}
                    </dl>
                  </section>

                  <section className="c-article__details c-article__details--maincol">
                    <h3>
                      <FormattedMessage {...resourceMessages.identification} />
                    </h3>
                    <dl className="c-article__defs">
                      {styleBodyType === "default" && (
                        <div className="list__def c-article__defs-col">
                          <dt>
                            <FormattedMessage
                              {...resourceMessages.objectType}
                            />
                          </dt>
                          <dd>{getResourceLabel(resource.type)}</dd>
                        </div>
                      )}
                      {this.renderCustomFieldsByPlacement(
                        customFields,
                        "main_body"
                      )}
                      {this.props.resource.source_id &&
                      isConTopMuseumProject ? (
                        // Display Source Id only for ConeTop Project
                        <div className="list__def c-article__defs-col">
                          <dt>Source Id</dt>
                          <dd>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: markdown.render(
                                  filterHTML(this.props.resource.source_id) ||
                                    ""
                                )
                              }}
                            />
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  </section>
                  {related &&
                    related.length > 0 && (
                      <div
                        className="c-article__related-items show-for-medium"
                        style={getStyles("c-article__related-items")}
                      >
                        <h3>
                          <FormattedMessage
                            {...resourceMessages.relatedItems}
                          />
                        </h3>
                        <div className="thumbnail-grid thumbnail-grid--main-column">
                          {related.map((relation, i) => (
                            <RelatedItem key={i} resource={relation} />
                          ))}
                        </div>
                      </div>
                    )}
                  {findingAidChildren &&
                    findingAidChildren.length > 0 && (
                      <div
                        className="c-article__related-items show-for-medium"
                        style={getStyles("c-article__related-items")}
                      >
                        <h3>
                          <FormattedMessage
                            {...resourceMessages.findingAidChildren}
                          />
                        </h3>
                        <div className="thumbnail-grid thumbnail-grid--main-column">
                          {findingAidChildren.map((resource, i) => (
                            <RelatedItem
                              key={`finding-item-${i}`}
                              resource={resource}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  {findingAidParents &&
                    findingAidParents.length > 0 && (
                      <div
                        className="c-article__related-items show-for-medium"
                        style={getStyles("c-article__related-items")}
                      >
                        <h3>
                          <FormattedMessage
                            {...resourceMessages.findingAidParent}
                          />
                        </h3>
                        <div className="thumbnail-grid thumbnail-grid--main-column">
                          {findingAidParents.map((resource, i) => (
                            <RelatedItem
                              key={`finding-parent-${i}`}
                              resource={resource}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                </div>
                <div
                  className="small-12 medium-5 xlarge-4 columns c-aticle__main-info"
                  style={getStyles("c-aticle__main-info")}
                >
                  <ArticleMetaShare
                    resource={resource}
                    type={resourceType}
                    active={activeResourceMedia}
                    isFavourite={this.props.isFavourite}
                    loggedIn={loggedIn}
                    showingModal={showingShareModal}
                    activeTab={shareModalTab}
                    closeModal={this.closeShareModal}
                  />

                  {canEdit && (
                    <a
                      target="_blank"
                      href={`${this._admin_url}/resource/${resource.id}`}
                      className={classNames(
                        "edit-link button button--lowlight button--small button--can-load",
                        { "state-loading": this.state.editLoading }
                      )}
                    >
                      <FormattedMessage {...resourceMessages.editItem} />
                    </a>
                  )}
                </div>
              </div>
              {related &&
                related.length > 0 && (
                  <div className="row show-for-small-only">
                    <div className="small-12 medium-7 columns">
                      <div className="c-article__related-items">
                        <h3>
                          <FormattedMessage
                            {...resourceMessages.relatedItems}
                          />
                        </h3>
                        <div className="thumbnail-grid thumbnail-grid--main-column">
                          {related.map((relation, i) => (
                            <RelatedItem key={i} resource={relation} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            {showTranscripts && (
              <div
                id="tab-item-transcript"
                className={classNames("c-tabs__content", {
                  "state-active": this.state.activeTab === "tab-item-transcript"
                })}
              >
                <AccordiTab
                  className="c-accorditabs--default-style"
                  tabs={false}
                  activeItems={this.state.activeTranscriptIndex}
                >
                  {transcript && (
                    <div
                      key={`transcript-${transcript.id}`}
                      dangerouslySetInnerHTML={{
                        __html: markdown.render(filterHTML(transcript.text))
                      }}
                    />
                  )}

                  {childTranscripts.map(transcript => (
                    <AccordiItem
                      key={transcript.id}
                      id={`transcript-${transcript.id}`}
                      title={
                        <span>
                          {getStyleTag(["badge", "badge-button"])}
                          <button
                            onClick={e => {
                              e.preventDefault()
                              this._setActiveChild(transcript.id)
                            }}
                            className="badge js-activate-image"
                          >
                            <FormattedMessage {...resourceMessages.showImage} />
                          </button>{" "}
                          <FormattedMessage
                            {...resourceMessages.imagePosition}
                            values={{ position: transcript.getPosition() }}
                          />
                        </span>
                      }
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: markdown.render(filterHTML(transcript.text))
                        }}
                      />
                    </AccordiItem>
                  ))}
                </AccordiTab>
              </div>
            )}

            <div
              id="tab-item-comments"
              className={classNames("c-tabs__content", {
                "state-active": this.state.activeTab === "tab-item-comments"
              })}
            >
              <div className="c-comment-list">
                <CommentsWidget id="iframe-comments" src={commentsIframeUrl} />
              </div>
            </div>
          </div>
        </article>
      </main>
    )
  }
}

export default Relay.createContainer(injectIntl(Resource), {
  initialVariables: {
    pageSize: 500,
    showingShareModal: false,
    shareModalTab: "share"
  },
  fragments: {
    resource: ({ showingShareModal, shareModalTab }) => Relay.QL`
      fragment on Resource {
        id
        source_id
        title
        type
        type_icon
        custom_fields
        ${PDFView.getFragment("resource")}
        ${AudioView.getFragment("resource")}
        ${VideoView.getFragment("resource")}
        ${ImageView.getFragment("resource")}
        ${MapView.getFragment("resource")}
        ${AddFavourite.getFragment("resource")}
        ${ArticleMetaShare.getFragment("resource")}
        ${ArticleMetaShare.getFragment("active", {
          showingModal: showingShareModal,
          activeTab: shareModalTab
        })}
        related {
          ${RelatedItem.getFragment("resource")}
        }
        finding_aid_children {
          ${RelatedItem.getFragment("resource")}
        }
        finding_aid_parents {
          ${RelatedItem.getFragment("resource")}
        }
        files {
          id
          type
          contents
          images {
            small { url height width }
            medium { url height width }
          }
          deep_zoom { url }
          video {
            med {
              url
              mime_type
            }
            low {
              url
              mime_type
            }
          }
          audio {
            lossy {
                url
                mime_type
            }
            waveform_json {
                url
            }
          }
          documents { pdf { url } }
        }
        children (first: $pageSize) {
          edges {
            node {
              id
              title
              type
              position
              ${PDFView.getFragment("resource")}
              ${ImageView.getFragment("resource")}
              ${ArticleMetaShare.getFragment("resource")}
              ${ArticleMetaShare.getFragment("active")}
              files {
                id
                type
                contents
                images {
                  small { url height width }
                  medium { url height width }
                }
                deep_zoom { url }
                documents { pdf { url } }
              }
            }
          }
          pageInfo {
            hasNextPage
          }
        }
      }`,
    viewer: () => Relay.QL`
        fragment on Viewer {
          ${PDFView.getFragment("viewer")}
          ${AddFavourite.getFragment("viewer")}
        }
      `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/Resource.js